import PropTypes from 'prop-types';

import usePagination from 'hooks/usePagination';

import { noop } from 'lib/utils';

import ArrowButton from './ArrowButton';
import PageLinkButton from './PageLinkButton';
import PaginationLinkButton from './PaginationLinkButton';

import ACTIONS from 'constants/action';

const TO_FIRST_PAGE = 'Primeira';
const TO_LAST_PAGE = 'Última';

const Pagination = ({
  total,
  page = 1,
  itemsBoundary,
  showFirstButton = true,
  showLastButton = true,
  showPrevButton = true,
  showNextButton = true,
  hrefFn = noop,
  onChange = noop,
  onFirstButtonClick = noop,
  onLastButtonClick = noop,
  onItemsClick = noop,
}) => {
  const { items } = usePagination({ page, total, itemsBoundary });

  const onFirstPageButtonClickHandler = (e) => {
    onFirstButtonClick(e);
    onChange(1);
  };

  const onLastPageButtonClickHandler = (e) => {
    onLastButtonClick(e);
    onChange(total);
  };

  const onPrevButtonClickHandler = () => onChange(page - 1);

  const onNextButtonClickHandler = () => onChange(page + 1);

  return (
    <div className="flex">
      {page > 1 && showFirstButton && (
        <>
          <PaginationLinkButton
            aria-label="Ir para primeira página"
            data-test-selector="goto-first-page"
            href={hrefFn(1)}
            onClick={onFirstPageButtonClickHandler}
          >
            {TO_FIRST_PAGE}
          </PaginationLinkButton>
          {showPrevButton && (
            <ArrowButton
              aria-label="Ir para página anterior"
              className="mr-1 px-2"
              data-test-selector="goto-previous-page"
              direction={ACTIONS.ARROW_PREV}
              href={hrefFn(page - 1)}
              onClick={onPrevButtonClickHandler}
            />
          )}
        </>
      )}
      {items.map(({ page: currentPage, isActive }) => (
        <PageLinkButton
          key={currentPage}
          aria-label={
            isActive ? 'Página atual' : `Ir para página ${currentPage}`
          }
          data-test-selector={
            isActive
              ? `current-page-${currentPage}`
              : `goto-page-${currentPage}`
          }
          href={hrefFn(currentPage)}
          isActive={isActive}
          onClick={(e) => {
            onItemsClick(e);
            onChange(currentPage);
          }}
        >
          {currentPage}
        </PageLinkButton>
      ))}
      {page < total && showLastButton && (
        <>
          {showNextButton && (
            <ArrowButton
              aria-label="Ir para a próxima página"
              className="ml-1 px-2"
              data-test-selector="goto-next-page"
              direction={ACTIONS.ARROW_NEXT}
              href={hrefFn(page + 1)}
              onClick={onNextButtonClickHandler}
            />
          )}
          <PaginationLinkButton
            aria-label="Ir para última página"
            data-test-selector="goto-last-page"
            href={hrefFn(total)}
            onClick={onLastPageButtonClickHandler}
          >
            {TO_LAST_PAGE}
          </PaginationLinkButton>
        </>
      )}
    </div>
  );
};

Pagination.propTypes = {
  /**
   * **DEVELOPMENT USE ONLY**
   */
  hrefFn: PropTypes.func,
  /**
   * Renders "first" button
   */
  showFirstButton: PropTypes.bool,
  /**
   * Renders "last" button
   */
  showLastButton: PropTypes.bool,
  /**
   * Renders "before page" arrow button
   */
  showPrevButton: PropTypes.bool,
  /**
   * Renders "next arrow" button
   */
  showNextButton: PropTypes.bool,
  /**
   * Renders the total of pages
   */
  total: PropTypes.number.isRequired,
  /**
   * Choose the selected page
   */
  page: PropTypes.number,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function called when page is changed
   */
  onChange: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function called when "first" page is clicked
   */
  onFirstButtonClick: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function called when "last" page is clicked
   */
  onLastButtonClick: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function called when a page is clicked
   */
  onItemsClick: PropTypes.func,
};

export default Pagination;
